import axios from 'axios'
import { Bugfender } from '@bugfender/sdk'
import { dispatch as busDispatch } from 'use-bus'
import store from '../store'
import {
    normalizeAxiosError,
    CanceledError,
    RequirePaymentError,
    TokenError,
} from './errors'

export const API_HOST = process.env.REACT_APP_API_HOST

const httpClient = axios.create({
    baseURL: `${API_HOST}/api`,
    timeout: 60000,
})

httpClient.interceptors.request.use((config) => {
    let token
    try {
        token = store.getState()?.auth?.session?.token
    } catch (_) {
        /* store not ready */
    }
    if (!token) {
        try {
            token = localStorage.getItem('token')
        } catch (_) {}
    }
    if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
    }

    // Let the browser set Content-Type (with multipart boundary) for FormData.
    if (
        typeof FormData !== 'undefined' &&
        config.data instanceof FormData &&
        config.headers
    ) {
        delete config.headers['Content-Type']
    }

    return config
})

httpClient.interceptors.response.use(
    (r) => r,
    (err) => Promise.reject(err)
)

/**
 * App-wide side effects fired off the back of API errors. Living in the HTTP
 * layer means both React Query paths and direct imperative callers get
 * identical 402/401 behavior.
 *
 * Each effect is idempotent (event-bus dispatch + `window.location.href` set
 * + `queryClient.clear()` later in the RQ path), so the RQ path firing the
 * same effects a second time on cached query errors is harmless.
 */
function applyErrorSideEffects(error) {
    if (!error || error instanceof CanceledError) return

    if (error instanceof RequirePaymentError) {
        try {
            busDispatch({
                type: 'subscriptionAddOnModal',
                payload: { entity: error.entity },
            })
        } catch (_) {}
        return
    }

    if (error instanceof TokenError) {
        try {
            if (
                typeof window !== 'undefined' &&
                !window.location.href.includes('login')
            ) {
                window.location.href = '/'
            }
        } catch (_) {}
        return
    }
}

/**
 * Execute a request and return the parsed body.
 * Errors are normalized via {@link normalizeAxiosError} and routed through
 * {@link applyErrorSideEffects} so 402 paywall + 401 sign-out semantics are
 * preserved no matter which call site triggers the request.
 *
 * @template T
 * @param {import('axios').AxiosRequestConfig} config
 * @returns {Promise<T>}
 */
export async function request(config) {
    try {
        const res = await httpClient.request(config)
        return res.data
    } catch (raw) {
        const err = normalizeAxiosError(raw)
        if (!(err instanceof CanceledError)) {
            try {
                Bugfender.error(
                    { url: config.url, method: config.method },
                    err
                )
            } catch (_) {}
        }
        applyErrorSideEffects(err)
        throw err
    }
}

/**
 * Verb helpers. Argument shape is `(url, params, data?, config?)` so call
 * sites can pass JSON:API params alongside an optional body.
 */
export const api = {
    get: (url, params, config = {}) =>
        request({ method: 'get', url, params, ...config }),
    post: (url, params, data, config = {}) =>
        request({ method: 'post', url, params, data, ...config }),
    put: (url, params, data, config = {}) =>
        request({ method: 'put', url, params, data, ...config }),
    patch: (url, params, data, config = {}) =>
        request({ method: 'patch', url, params, data, ...config }),
    delete: (url, params, data, config = {}) =>
        request({ method: 'delete', url, params, data, ...config }),
}

export { httpClient }
